// Terms of Use, Privacy Policy and Cookie Policy of the portal (pages /terms, /privacy, /cookies; LegalPage.vue).
// The same text is used by every frontend; who is responsible and the contact details come from ./org.js.
// Facts about hosting, storage and service providers were checked against the code and the servers on
// 2026-10-21 (see backendhip CLAUDE.md). Change this file in every frontend at once.

export const UPDATED = "21 October 2026";
export const VERSION = "1.0";

// "the FCT Health Insurance Scheme (FHIS)" or the generic description on the shared Claimify portal.
function agencyFull(org) {
  const a = org.agency;
  return a.short ? `the ${a.name} (${a.short})` : a.name;
}

function contactBlocks(org) {
  const a = org.agency;
  const lines = [];
  if (a.dpoEmail) lines.push(`Data Protection Officer: ${a.dpoEmail}`);
  if (a.email) lines.push(`Email: ${a.email}`);
  if (a.phone) lines.push(`Telephone: ${a.phone}`);
  if (a.address) lines.push(`Address: ${a.address}`);
  if (a.website) lines.push(`Website: ${a.website}`);
  const blocks = [];
  if (lines.length) {
    blocks.push({ p: `${a.short || "The Agency"}:` }, { ul: lines });
  } else {
    blocks.push({ p: "The Agency: contact its Data Protection Officer through the Agency's offices or the contact details on its official website." });
  }
  blocks.push(
    { p: `${org.operator.name} (operator of ${org.platform}, for technical and security questions):` },
    { ul: [`Email: ${org.operator.email}`] },
  );
  return blocks;
}

// Service providers that process information for the portal, with where they are (checked 2026-10-21).
const PROVIDERS = [
  ["DigitalOcean", "Servers that run the portal's application and database", "United Kingdom (London data centre); US company"],
  ["Google (Firebase Hosting, Google Fonts)", "Delivers the portal's web pages and fonts to your browser; sees your IP address, not your records", "Global network; US company"],
  ["Cloudflare (cdnjs)", "Delivers some page scripts and styles to your browser; sees your IP address", "Global network; US company"],
  ["Mailtrap, Plunk", "Sending emails (notifications, reports, password resets)", "Outside Nigeria"],
  ["Bulk SMS gateway, Twilio", "Sending SMS and WhatsApp messages (reminders, codes, notices)", "Nigeria and outside Nigeria"],
  ["Paystack, Flutterwave, CoralPay", "Online payments (contributions, renewals, consultation fees)", "Nigeria (some processing outside Nigeria)"],
  ["Safe Haven Microfinance Bank, Sudo", "Bank and wallet services for paying health facilities", "Nigeria"],
  ["Agora", "Audio and video for telemedicine consultations", "Outside Nigeria"],
  ["HIVA TRACE", "Checking claims against tariffs and clinical rules; receives coded identifiers, never names, phone numbers or ID card numbers", "May be outside Nigeria"],
  ["hefrias", "Health facility self-assessment forms (facility information only)", "May be outside Nigeria"],
];

// Retention schedule shown in the Privacy Policy. Periods must match the Agency's approved retention schedule;
// an org.js may replace the whole table with its own (org.retention).
export const RETENTION = [
  ["Enrolment records of principals and dependants", "While covered, then 6 years", "End of the last cover or exit from the scheme"],
  ["Fingerprints and identification photographs", "While covered, then 12 months", "Exit from the scheme"],
  ["Health records: encounters, referrals, authorization codes, telemedicine", "10 years (for a child, until age 25 if later)", "The last entry"],
  ["Claims, payment orders, contributions and payments", "6 years", "End of the financial year in which the claim or payment was closed"],
  ["Complaints and tickets", "2 years, longer while an appeal or legal claim is pending", "Closure of the complaint"],
  ["Quality assurance and accreditation submissions", "6 years", "Submission"],
  ["Staff, facility and other user accounts", "While active, then 2 years", "Deactivation of the account"],
  ["Record of sign-ins and changes (audit log)", "6 years", "The sign-in or change"],
  ["In-app messages", "3 years", "Sending"],
  ["Server and application logs", "14 days", "The request"],
  ["Exported files and generated reports", "30 days", "Creation"],
  ["One-time codes; password reset links", "10 minutes; 60 minutes", "Issue"],
];

export function privacy(org) {
  const A = org.agency.short || "the Agency";
  return {
    key: "privacy",
    title: "Privacy Policy",
    summary: `How ${agencyFull(org)} and ${org.operator.name} collect, use, share, protect and keep personal information on ${org.portal}, and the rights you have over it.`,
    sections: [
      {
        id: "who-we-are",
        heading: "Who we are",
        blocks: [
          { p: `In this policy, "the Agency" means ${agencyFull(org)}${org.agency.genericNote ? ` ${org.agency.genericNote}` : ""}. The Agency runs the health insurance programme you are enrolled in or work with, and decides why and how your personal information is used. Under the Nigeria Data Protection Act 2023 (NDPA) it is the data controller.` },
          { p: `${org.operator.name} provides and operates ${org.platform}, the software behind ${org.portal}, on the Agency's behalf. It is a data processor: it acts only on the Agency's documented instructions, keeps the information confidential and secure, and does not use it for its own purposes.` },
          { p: "A health facility that registers on the portal independently of any agency, and the patients it records, is responsible for its own patients' information in the same way an agency is." },
          { p: "This policy covers the web portal, its mobile app, USSD and SMS services, offline enrolment and the interfaces other systems use to exchange information with the portal." },
        ],
      },
      {
        id: "information-we-collect",
        heading: "Information we collect",
        blocks: [
          { p: "What we hold depends on how you use the portal:" },
          { ul: [
            "Enrollees and dependants: name, sex, date of birth, marital status, photograph, fingerprints, National Identification Number (NIN), enrollee ID number, phone number, email, home address, state, local government area, ward and community, programme or sector, employer or establishment (MDA), salary or staff number, chosen health facility, HMO or plan, cover start and expiry dates, and your dependants' details.",
            "Health information: visits (encounters), reasons for visit, vital signs, history, examination findings, family history and allergies, diagnoses, investigations and results, treatments, drugs and services given, referrals and authorization codes, telemedicine consultations, appointments, claims, and a photograph taken at a visit to confirm your identity.",
            "Payment information: contributions, renewals, consultation fees and their payment references. Card details are entered on the payment provider's page and never reach the portal.",
            "Health facility, HMO and vendor information: facility details, licences and documents, staff accounts, bank or wallet details for payments, and the identity details of a director needed to open a payment account (such as BVN or NIN, checked by one-time code).",
            "Staff and user accounts: name, contact details, job title, role, employer, sign-in records and a log of the changes each account makes.",
            "Messages and requests: in-app messages, complaints and tickets, quality assurance submissions, and enquiries sent through our website.",
            "Technical information: your IP address, browser and device type and the time of each request, recorded in server logs for security.",
          ] },
          { p: "Health information, fingerprints, photographs used for identification and NIN are sensitive personal information under the NDPA. We collect them only because health insurance cannot be run without them, and we protect them accordingly." },
        ],
      },
      {
        id: "sources",
        heading: "Where the information comes from",
        blocks: [
          { ul: [
            "From you, when you enrol, renew, update your details, book a consultation or contact us.",
            "From the person enrolling you, such as an Agency enrolment officer, your employer or MDA, or the principal enrollee for a dependant.",
            "From health facilities when you receive care, and from facilities you are referred to.",
            "From HMOs and third-party administrators that manage your plan, and from payment providers when you pay.",
            "From the Agency's own records and from other public bodies where the law allows.",
          ] },
        ],
      },
      {
        id: "how-we-use",
        heading: "How we use it and our legal basis",
        blocks: [
          { p: "We use personal information only for the purposes below, each with a lawful basis under section 25 of the NDPA and, for sensitive information, a condition under section 30." },
          { table: {
            head: ["Purpose", "Legal basis"],
            rows: [
              ["Enrolling you and your dependants, issuing ID cards and confirming you are covered when you seek care", "Performing the Agency's statutory function and your membership; legal obligation"],
              ["Recording care, issuing authorization codes and referrals, and paying health facilities for the care you receive", "Medical and health insurance purposes under professional confidentiality; legal obligation"],
              ["Checking claims for errors, fraud and abuse, including automated checks", "Legal obligation and the Agency's public function of protecting the fund"],
              ["Collecting contributions and renewals and paying facilities online", "Your membership; legal obligation"],
              ["Telemedicine consultations", "Medical purposes; your request for the consultation"],
              ["Sending reminders, codes and notices about your cover by SMS, email or in-app message", "Your membership and the Agency's public function"],
              ["Handling complaints, quality assurance and facility accreditation", "The Agency's public function; legal obligation"],
              ["Statistics, planning, disease surveillance and reports to supervising authorities", "The Agency's public function; public interest in health. Reports use counts and summaries, not names, wherever possible"],
              ["Keeping the portal secure, investigating misuse and keeping records of who changed what", "Legitimate interest in security; legal obligation"],
            ],
          } },
          { p: "Where a purpose depends on your consent, we will ask for it clearly and you can withdraw it at any time without affecting what was done before. We do not sell personal information and we do not use it for advertising." },
        ],
      },
      {
        id: "automated-decisions",
        heading: "Automated checks",
        blocks: [
          { p: "The portal uses automated tools to help staff: rule-based claim vetting, claim fraud signals, duplicate-enrolment checks, disease alerts and external claim vetting by HIVA TRACE. These tools flag records for attention. A member of staff makes the decision, and no decision with legal or similarly significant effect on you is made by automated means alone. You can ask for any decision to be reviewed by a person." },
        ],
      },
      {
        id: "sharing",
        heading: "Who we share it with",
        blocks: [
          { ul: [
            `Agency staff, only as far as their job needs. Access is controlled by role and every change is logged.`,
            "The health facility you are registered with or attend, facilities you are referred to, and telemedicine doctors who consult you.",
            "The HMO or third-party administrator that manages your plan, where you have one.",
            "The National Health Insurance Authority and other public bodies, where a law requires or permits it.",
            "Courts, the police or regulators, where we are legally required to.",
            `Service providers that process information for us under written contracts (listed below).`,
          ] },
          { p: "Every service provider is bound by a written agreement covering instructions, confidentiality, security, help with your rights, breach reporting, and return or deletion of information when the service ends." },
          { table: { head: ["Service provider", "What it does for us", "Where"], rows: PROVIDERS } },
        ],
      },
      {
        id: "international-transfers",
        heading: "Storage and international transfers",
        blocks: [
          { p: `The portal's application and database run on servers in London, United Kingdom, provided by DigitalOcean. ${A}'s information held on the portal is therefore stored outside Nigeria. The portal's web pages are delivered to your browser by Google Firebase Hosting and Cloudflare, which carry the page files, not your records.` },
          { p: "Some service providers listed above also process information outside Nigeria. Before information leaves Nigeria we make sure there is a lawful basis for the transfer under sections 41 to 43 of the NDPA, such as adequate data protection law in the destination country or appropriate safeguards in the contract, and we apply the security measures described below. Using a foreign provider does not reduce our obligations to you." },
          { p: "You can ask the Agency's Data Protection Officer for details of the transfers and safeguards that apply to your information." },
        ],
      },
      {
        id: "security",
        heading: "How we protect it",
        blocks: [
          { ul: [
            "Encrypted connections (HTTPS) between your device and the portal.",
            "Personal sign-in for every user; sign-in tokens expire after 12 hours; one-time codes expire after 10 minutes and are limited to five attempts.",
            "Role-based access: staff see only their agency's records, and facilities only their own patients and claims.",
            "A record of every sign-in and of who created, changed or deleted records, kept for audit.",
            "Pseudonymous identifiers when claims are sent for external vetting.",
            "Server access limited to authorised technical staff.",
            "Confidentiality duties and training for staff with access.",
          ] },
          { p: "We review these measures as risks and systems change. No system can be completely secure, which does not reduce our duty to protect your information." },
        ],
      },
      {
        id: "breaches",
        heading: "If something goes wrong",
        blocks: [
          { p: "If a personal data breach is likely to put your rights and freedoms at risk, the Agency will notify the Nigeria Data Protection Commission within 72 hours of becoming aware of it. Where the risk to you is high, we will also tell you directly, in plain language, what happened, what it means for you and what we are doing about it. Our service providers must report breaches to us without delay." },
        ],
      },
      {
        id: "retention",
        heading: "How long we keep it",
        blocks: [
          { p: "We keep personal information only as long as it is needed for the purpose it was collected for, or for as long as a law requires. When a period ends, the information is securely deleted or permanently anonymised. Disposal is suspended for records needed for an investigation, dispute or court case until it is resolved." },
          { table: { head: ["Record", "How long", "From"], rows: org.retention || RETENTION } },
          { p: "When you leave the scheme, your active profile is deactivated so it can no longer be used for cover, while the records the Agency must keep are preserved for the periods above." },
        ],
      },
      {
        id: "children",
        heading: "Children",
        blocks: [
          { p: "Children are enrolled as dependants by a parent or guardian, usually the principal enrollee, who provides their information and exercises their rights on their behalf, as section 31 of the NDPA requires. Children's information is used only for their cover and care." },
        ],
      },
      {
        id: "your-rights",
        heading: "Your rights",
        blocks: [
          { p: "Subject to the conditions in the NDPA, you have the right to:" },
          { ul: [
            "be told how your information is used (this policy);",
            "get a copy of your information;",
            "have inaccurate or incomplete information corrected;",
            "have information erased or its use restricted, where it is no longer needed or is used unlawfully;",
            "object to uses based on the Agency's public function or legitimate interests;",
            "withdraw consent you have given;",
            "receive information you provided in a commonly used electronic format, where applicable;",
            "not be subject to a decision based solely on automated processing that significantly affects you.",
          ] },
          { p: "Some rights are limited where the Agency must keep records by law, for example claims and payment records. To use a right, contact the Agency (details below). We may ask you to confirm your identity. We will respond without undue delay and within the time the law allows, and we will not charge a fee unless a request is clearly excessive." },
          { p: "You can also complain to the Nigeria Data Protection Commission (ndpc.gov.ng). We would welcome the chance to resolve your concern first." },
        ],
      },
      {
        id: "cookies",
        heading: "Cookies and browser storage",
        blocks: [
          { p: "The portal does not use cookies to track you or for advertising. It keeps a few items in your browser's storage so that you stay signed in and the pages work. The Cookie Policy lists them." },
        ],
      },
      {
        id: "your-responsibilities",
        heading: "Your responsibilities and links to other sites",
        blocks: [
          { p: "Please give accurate information, tell us when it changes, and keep your sign-in details confidential. Organisations submitting information about others must have the authority to do so and take reasonable steps to make sure it is accurate. Inaccurate or incomplete information may delay verification or care." },
          { p: "The portal may link to other websites, which have their own privacy practices. A link does not make us responsible for how another organisation uses information it collects." },
        ],
      },
      {
        id: "changes",
        heading: "Changes to this policy",
        blocks: [
          { p: "We will update this policy when our practices or the law change, and show the date of the latest version at the top. Where a change materially affects how your information is used, we will tell you through the portal or by message before it takes effect." },
        ],
      },
      { id: "contact", heading: "Contact us", blocks: contactBlocks(org) },
    ],
  };
}

export function terms(org) {
  return {
    key: "terms",
    title: "Terms of Use",
    summary: `The rules for using ${org.portal}: accounts, permitted use, confidentiality, claims and payments, and the responsibilities of everyone who uses it.`,
    sections: [
      {
        id: "about",
        heading: "About these terms",
        blocks: [
          { p: `These terms apply to everyone who uses ${org.portal}: enrollees, Agency staff, health facilities and their staff, HMOs and third-party administrators, telemedicine doctors, vendors, and organisations that connect their systems to it. "The Agency" means ${agencyFull(org)}${org.agency.genericNote ? ` ${org.agency.genericNote}` : ""}. ${org.operator.name} provides and operates the ${org.platform} software for the Agency.` },
          { p: "By signing in or using the portal you agree to these terms, the Privacy Policy and the Cookie Policy. If you use the portal for an organisation, you confirm you are authorised to accept these terms for it. Agreements your organisation has with the Agency, such as a facility's service agreement, also apply and prevail where they conflict with these terms." },
        ],
      },
      {
        id: "accounts",
        heading: "Accounts and security",
        blocks: [
          { ul: [
            "Accounts are personal. Do not share your password, one-time codes or signed-in device with anyone, including colleagues.",
            "Use a strong password, sign out on shared devices, and keep the devices you use for the portal locked and up to date.",
            "Tell the Agency or your administrator at once if you think someone else has used your account or seen information they should not have.",
            "You are responsible for what is done with your account until you report it compromised.",
            "Administrators must give staff only the roles their job needs and remove access promptly when a person changes role or leaves.",
          ] },
        ],
      },
      {
        id: "permitted-use",
        heading: "Permitted use and confidentiality",
        blocks: [
          { p: "Use the portal only for the health insurance and health care purposes your role covers. Personal and health information you see is confidential:" },
          { ul: [
            "look up, view or change a record only when your work requires it;",
            "share information only with people entitled to it, and only what they need;",
            "download or print records only when necessary, keep the files secure, and delete them when no longer needed;",
            "never copy information into personal email, messaging apps or unapproved tools;",
            "report any suspected misuse or breach to the Agency immediately.",
          ] },
          { p: "Sign-ins and every change to records are logged. Misuse may lead to suspension, disciplinary action under your employment or service agreement, and liability under the Nigeria Data Protection Act 2023 and other laws." },
        ],
      },
      {
        id: "enrollees",
        heading: "Enrollees",
        blocks: [
          { ul: [
            "Give accurate information when you enrol and renew, and tell the Agency when your details, dependants or employer change.",
            "Your ID card and enrollee number are for you and your registered dependants only. Lending them to anyone else is fraud.",
            "Present your ID or verify your identity when a facility asks; facilities may take a photograph to confirm it is you.",
            "Your benefits, tariffs, cover period and facility rules are set by the Agency's scheme and may change. The portal shows them but does not create them.",
          ] },
        ],
      },
      {
        id: "facilities",
        heading: "Health facilities, HMOs and their staff",
        blocks: [
          { ul: [
            "Record encounters, referrals and claims accurately and promptly, only for care actually given to the enrollee identified.",
            "Verify the enrollee's identity and cover before providing care billed to the scheme.",
            "Submitting false, inflated or duplicate claims, or claims for services not rendered, is prohibited. Claims are checked by the Agency, including by automated tools, and may be queried, reduced or rejected.",
            "Keep your facility details, licences and payment account details up to date. Online payments are made only to accounts that pass the bank's name check for the facility.",
            "Your own duties of professional confidentiality and record keeping continue to apply.",
          ] },
        ],
      },
      {
        id: "claims-payments",
        heading: "Claims and payments",
        blocks: [
          { ul: [
            "Claims are approved and paid according to the Agency's rules, tariffs and timelines. Status shown on the portal is for information; the Agency's decision is final, subject to its review and appeal procedures.",
            "Online payments to facilities are prepared and approved by different authorised Agency officers and are made through regulated payment providers.",
            "Contributions, renewals and consultation fees paid online are processed by the payment provider shown at checkout. Refunds follow the Agency's or facility's refund rules.",
            "Report payment errors to the Agency promptly. Money paid in error must be returned.",
          ] },
        ],
      },
      {
        id: "telemedicine",
        heading: "Telemedicine",
        blocks: [
          { p: "Telemedicine consultations are given by licensed health professionals who are responsible for their clinical advice. A remote consultation has limits and the doctor may ask you to attend a facility in person." },
          { p: "Telemedicine is not for emergencies. In an emergency, go to the nearest hospital or call the emergency number 112." },
        ],
      },
      {
        id: "tools",
        heading: "Automated and assistive tools",
        blocks: [
          { p: "The portal includes tools that suggest, check or flag: claim vetting, fraud signals, duplicate checks, diagnosis search, disease alerts, forecasts and reports. They help people decide; they do not replace professional judgement or the Agency's decisions. Check their output before relying on it." },
        ],
      },
      {
        id: "marketplace",
        heading: "Vendors and the marketplace",
        blocks: [
          { p: "Where the portal offers drug procurement or a marketplace, vendors are responsible for the products they list, their quality, regulatory approval, prices, delivery and after-sales service. Orders are agreements between the buyer and the vendor unless the Agency is the buyer." },
        ],
      },
      {
        id: "prohibited",
        heading: "What you must not do",
        blocks: [
          { ul: [
            "access or try to access records, accounts or parts of the portal you are not authorised to use;",
            "use automated means to copy, scrape or bulk-download information, except through interfaces the Agency has approved for you;",
            "interfere with the portal's security or operation, upload malicious code, or test its security without written permission;",
            "copy, modify, reverse engineer or resell the software;",
            "use the portal to harass anyone, send unsolicited messages, or for any unlawful purpose;",
            "enter information you know to be false.",
          ] },
        ],
      },
      {
        id: "availability",
        heading: "Availability and offline use",
        blocks: [
          { p: "We aim to keep the portal available at all times but cannot promise it will be free of interruptions. Planned maintenance is done at quiet times where possible." },
          { p: "Offline enrolment and records are kept on your device until they are synchronised. Synchronise promptly, keep the device secure, and do not clear the browser's data before synchronising, or the records may be lost." },
        ],
      },
      {
        id: "intellectual-property",
        heading: "Intellectual property",
        blocks: [
          { p: `The ${org.platform} software, design and documentation belong to ${org.operator.name} or its licensors. The Agency's name, logo and scheme materials belong to the Agency. Information entered about enrollees and care belongs to the Agency and the people it concerns. Nothing in these terms gives you a licence beyond using the portal as these terms allow.` },
        ],
      },
      {
        id: "suspension",
        heading: "Suspension and ending access",
        blocks: [
          { p: "The Agency may suspend or end an account, halt enrolment by a user, or restrict access where these terms are broken, misuse or fraud is suspected, security requires it, or a person's role ends. Where it is safe and lawful to do so, we will tell you why. Records created through the account are kept as the Privacy Policy describes." },
        ],
      },
      {
        id: "liability",
        heading: "Disclaimers and liability",
        blocks: [
          { p: "The portal is provided to support the Agency's scheme. To the extent the law allows, neither the Agency nor the operator is liable for loss caused by information entered incorrectly by users, by your misuse of the portal or your account, by interruptions outside reasonable control, or by third-party services you choose to use. Nothing in these terms limits liability that cannot be limited by law, including for negligence causing death or personal injury, or limits anyone's duties under the Nigeria Data Protection Act 2023." },
          { p: "Organisations that use the portal are responsible for the acts of their staff and for loss caused by breaking these terms." },
        ],
      },
      {
        id: "changes",
        heading: "Changes to these terms",
        blocks: [
          { p: "We may update these terms when the portal, the scheme or the law changes. The date of the latest version is shown at the top. Significant changes will be announced on the portal; continuing to use the portal after that means you accept them." },
        ],
      },
      {
        id: "law",
        heading: "Governing law and disputes",
        blocks: [
          { p: `These terms are governed by ${org.governingLaw}. Raise any complaint or dispute first with the Agency through its complaints process on the portal or its offices. If it is not resolved, the parties will try mediation before going to a court of competent jurisdiction in Nigeria.` },
        ],
      },
      { id: "contact", heading: "Contact us", blocks: contactBlocks(org) },
    ],
  };
}

// What the portal keeps in the browser (checked in the code of every frontend on 2026-10-21).
const STORAGE = [
  ["jwt, token", "Your sign-in token, so you stay signed in", "Strictly necessary", "Until you sign out; the token stops working after 12 hours"],
  ["user", "Your name, role and account settings, to show the right menus", "Strictly necessary", "Until you sign out"],
  ["agency", "The Agency's name, logo and portal settings", "Strictly necessary", "Until you clear your browser's data"],
  ["reloaded", "Refreshes the page once after sign-in", "Strictly necessary", "Removed straight away"],
  ["self_service_access (session storage)", "Keeps an enrollee's self-service access open in this tab", "Strictly necessary", "Until the tab is closed"],
  ["hospital_data (IndexedDB), synctotal", "Encounters and enrolments recorded offline, waiting to be synchronised", "Strictly necessary", "Until they are synchronised"],
  ["snap", "The photo just taken during enrolment, before it is uploaded", "Strictly necessary", "Until the next photo or until you clear your browser's data"],
  ["enrollee, dependent, clients, editUser, facility, facilities, provider_id, phone_number, sector, services, drugs, diseases, wards_data, selected_lga", "The record, list or choice you are working on, passed from one page to the next", "Strictly necessary", "Replaced as you work; until you clear your browser's data"],
  ["marketplace_cart, independent", "Your marketplace cart; whether your facility is independent (only where these features exist)", "Functional", "Until you empty the cart or sign out"],
];

export function cookies(org) {
  return {
    key: "cookies",
    title: "Cookie Policy",
    summary: `What ${org.portal} stores on your device, why, who provides it and how long it stays.`,
    sections: [
      {
        id: "summary",
        heading: "In short",
        blocks: [
          { p: "The portal does not use cookies or similar tools to track your visits, build a profile of you or show advertising, and it does not use analytics services. It stores a few items in your browser so that you stay signed in, the pages work and offline records are not lost. Because these items are strictly necessary for the service you asked for, they do not need your consent, but you can remove them at any time." },
        ],
      },
      {
        id: "what-they-are",
        heading: "Cookies and browser storage",
        blocks: [
          { p: "Cookies are small files a website saves in your browser. Browsers also offer local storage, session storage and IndexedDB, which websites use in the same way. This policy covers all of them." },
        ],
      },
      {
        id: "what-we-store",
        heading: "What the portal stores",
        blocks: [
          { p: `All items below are set by ${org.portal} itself (first party) and are read only by it. The portal's server does not set cookies for signed-in use: you are recognised by the sign-in token your browser sends with each request.` },
          { table: { head: ["Name", "What it does", "Type", "How long it stays"], rows: STORAGE } },
        ],
      },
      {
        id: "third-parties",
        heading: "Third-party services",
        blocks: [
          { p: "Some features load content from other providers. They do not receive your records, but like any website they see your IP address and may set their own cookies under their own policies:" },
          { ul: [
            "Google Fonts and Cloudflare (cdnjs) deliver fonts and page scripts. Google states that Google Fonts does not set cookies.",
            "Payment providers (such as Paystack) show their checkout only when you choose to pay online. Their cookies are used for payment security and fraud prevention.",
            "Agora carries audio and video when you join a telemedicine consultation.",
          ] },
        ],
      },
      {
        id: "control",
        heading: "Your choices",
        blocks: [
          { ul: [
            "Signing out removes your sign-in token and account details.",
            "You can remove everything the portal has stored by clearing this site's data in your browser settings. You will then need to sign in again.",
            "Do not clear the site's data on a device with offline records that have not been synchronised; they would be lost.",
            "On shared devices, always sign out and clear the site's data when you finish.",
          ] },
          { p: "If the portal ever adds cookies that are not strictly necessary, such as analytics, they will stay off until you accept them, you will be able to refuse them without losing access to the core service, and this policy will be updated first." },
        ],
      },
      {
        id: "changes",
        heading: "Changes to this policy",
        blocks: [
          { p: "We update this policy when what the portal stores changes. The date of the latest version is shown at the top." },
        ],
      },
      { id: "contact", heading: "Contact us", blocks: contactBlocks(org) },
    ],
  };
}

export const DOCUMENTS = [
  { key: "privacy", path: "/privacy", build: privacy, title: "Privacy Policy" },
  { key: "terms", path: "/terms", build: terms, title: "Terms of Use" },
  { key: "cookies", path: "/cookies", build: cookies, title: "Cookie Policy" },
];
